<script setup lang="ts">
import { storeToRefs } from 'pinia';
import AddIcon from '@/dashboard/vue/components/icons/AddIcon.vue';
import DeleteIcon from '@/dashboard/vue/components/icons/DeleteIcon.vue';
import Divider from '@/dashboard/vue/components/common/Divider.vue';
import LogoutIcon from '@/dashboard/vue/components/icons/LogoutIcon.vue';
import { useAppStore } from '@/dashboard/store/appStore';
import SettingsSection from '@/dashboard/vue/components/settings/SettingsSection.vue';
import { useSettingsStore } from '@/dashboard/store/settingsStore';

const appStore = useAppStore();
const settingsStore = useSettingsStore();

const { currentUser } = storeToRefs(appStore);
const { isShowAvatarLoader } = storeToRefs(settingsStore);

async function fileChanged(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input?.files?.[0];

  if (file) {
    await settingsStore.changeAvatar(file);
    input.value = '';
  }
}

async function deleteAvatar() {
  await settingsStore.removeAvatar();
}
</script>

<template>
  <SettingsSection class="justify-center !px-zero !pt-zero pb-l laptop:h-[331px] laptop:pb-zero">
    <div class="flex w-full flex-col items-center px-m">
      <span class="h5 my-l">Hello, {{ currentUser?.name }}!</span>

      <div class="relative mb-m h-[88px] w-[88px] overflow-hidden rounded-[44px]">
        <img :src="currentUser?.image" alt="user image" class="h-[88px] w-[88px]" />

        <div v-if="isShowAvatarLoader" class="absolute left-zero top-zero flex h-full w-full items-center justify-center bg-black/70">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 22 22" fill="none" class="rotate">
            <path
              class="stroke-white"
              d="M20.5832 11C20.5832 16.2927 16.2926 20.5834 10.9998 20.5834C5.70711 20.5834 1.4165 16.2927 1.4165 11C1.4165 5.70729 5.70711 1.41669 10.9998 1.41669"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
      </div>

      <div class="flex w-full flex-col dark:text-gray-500 laptop:mb-l laptop:w-auto laptop:gap-y-xs">
        <div class="flex flex-col items-center gap-x-s laptop:flex-row">
          <div class="flex h-[65px] items-center laptop:h-auto">
            <span>Profile picture:</span>
          </div>

          <Divider class="flex w-full laptop:hidden" />

          <div class="flex h-[65px] items-center laptop:h-auto">
            <label class="flex cursor-pointer items-center gap-x-xs">
              <AddIcon class="h-s w-s stroke-black dark:stroke-gray-500" />
              <span>Upload</span>
              <input type="file" accept="image" class="absolute left-zero top-zero h-zero w-zero opacity-0" @change="fileChanged" />
            </label>
          </div>

          <span class="hidden laptop:block">/</span>

          <Divider class="flex w-full laptop:hidden" />

          <div class="flex h-[65px] items-center laptop:h-auto">
            <button class="flex items-center gap-x-xs" @click="deleteAvatar">
              <DeleteIcon class="h-s w-s stroke-black dark:stroke-gray-500" />
              <span>Delete</span>
            </button>
          </div>
        </div>

        <Divider />
      </div>

      <div class="flex h-[65px] items-center dark:text-gray-500 laptop:h-auto">
        <a href="/sign_out" class="flex items-center gap-x-xs">
          <LogoutIcon class="h-s w-s stroke-black dark:stroke-gray-500" />
          <span>Logout</span>
        </a>
      </div>
    </div>
  </SettingsSection>
</template>
